/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import i18n from '../../i18n';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  APILedgerEntriesResponseInterface,
  LedgerEntry,
  RequestLedgerEntriesInterface,
  TapCardLedgerInitialProperties,
} from '../../interface/rfid-management.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import rfidApi from '../../utils/api/rfid';

import { setNotificationMessage } from './NotificationManagementSlice';

/**
 * Normalizes raw ledger entry data from API responses
 * @param {any} item - Raw ledger entry data object
 * @returns {LedgerEntry} Normalized LedgerEntry entity
 */
const formatLedgerItem = (item: any): LedgerEntry => {
  let amount = 0;
  const rawAmount = item?.amountKwh;
  if (typeof rawAmount === 'object' && rawAmount !== null && '$numberDecimal' in rawAmount) {
    amount = Number.parseFloat(rawAmount.$numberDecimal) || 0;
  } else if (typeof rawAmount === 'number') {
    amount = rawAmount;
  } else if (typeof rawAmount === 'string') {
    amount = Number.parseFloat(rawAmount) || 0;
  }

  const rawTimestamp = item?.createdAt || item?.timestamp;
  const formattedTimestamp = rawTimestamp ? ConvertUTCtoDate(rawTimestamp) : '';

  return {
    ...item,
    id: item?.id || item?._id || '',
    _id: item?._id || item?.id || '',
    amountKwh: amount,
    timestamp: formattedTimestamp,
    createdAt: item?.createdAt ? ConvertUTCtoDate(item.createdAt) : formattedTimestamp,
    updatedAt: item?.updatedAt ? ConvertUTCtoDate(item.updatedAt) : '',
  };
};

/**
 * Async thunk to fetch all tap card ledger entries with query parameters
 */
export const fetchAllLedgerEntries = createAsyncThunk<
  APILedgerEntriesResponseInterface,
  RequestLedgerEntriesInterface
>(
  'tap-card-ledger/fetchAllLedgerEntries',
  async (parameters: RequestLedgerEntriesInterface, { dispatch }) => {
    try {
      const response = await rfidApi.fetchLedgerEntries(removeEmptyKeys(parameters));
      const rawResults =
        response?.data?.results ||
        response?.data?.data ||
        (Array.isArray(response?.data) ? response?.data : []);

      const formattedList: LedgerEntry[] = Array.isArray(rawResults)
        ? rawResults.map((item: any) => formatLedgerItem(item))
        : [];

      return {
        results: formattedList,
        limit: response?.data?.limit || parameters.limit || DefaultApiParameters.limit,
        page: response?.data?.page || parameters.page || DefaultApiParameters.page,
        totalPages: response?.data?.totalPages || 0,
        totalResults: response?.data?.totalResults || formattedList.length,
      };
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || i18n.t('devices:failedToFetchLedgerEntries'),
          code: error?.response?.data?.code || 500,
          status: true,
        }),
      );
      throw error;
    }
  },
);

/**
 * Async thunk to refresh ledger entries list
 */
export const refreshLedgerEntries = createAsyncThunk<
  APILedgerEntriesResponseInterface,
  RequestLedgerEntriesInterface
>('tap-card-ledger/refreshLedgerEntries', async (parameters: RequestLedgerEntriesInterface) => {
  const response = await rfidApi.fetchLedgerEntries(removeEmptyKeys(parameters));
  const rawResults =
    response?.data?.results ||
    response?.data?.data ||
    (Array.isArray(response?.data) ? response?.data : []);

  const formattedList: LedgerEntry[] = Array.isArray(rawResults)
    ? rawResults.map((item: any) => formatLedgerItem(item))
    : [];

  return {
    results: formattedList,
    limit: response?.data?.limit || parameters.limit || DefaultApiParameters.limit,
    page: response?.data?.page || parameters.page || DefaultApiParameters.page,
    totalPages: response?.data?.totalPages || 0,
    totalResults: response?.data?.totalResults || formattedList.length,
  };
});

/**
 * Async thunk to fetch a single ledger entry by ID
 */
export const fetchLedgerById = createAsyncThunk<LedgerEntry, string, { rejectValue: string }>(
  'tap-card-ledger/fetchLedgerById',
  async (ledgerId: string, { rejectWithValue }) => {
    try {
      const response = await rfidApi.fetchLedgerById(ledgerId);
      const data = response?.data?.results || response?.data?.data || response?.data || response;
      return formatLedgerItem({ ...data, id: data?.id || data?._id || ledgerId });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        i18n.t('devices:failedToFetchLedgerEntryDetails');
      return rejectWithValue(errorMessage);
    }
  },
);

/**
 * Async thunk to fetch ledger filter options
 */
export const fetchLedgerFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'tap-card-ledger/fetchLedgerFilters',
  async (_, { dispatch }) => {
    try {
      const response = await rfidApi.fetchLedgerFilterList();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || i18n.t('sharedComponent:somethingWentWrong'),
          },
        },
      });
      throw error;
    }
  },
);

/**
 * Compatibility exports matching legacy naming
 */
export const fetchLedgerEntriesApi = fetchAllLedgerEntries;
export const fetchLedgerByIdApi = fetchLedgerById;
export const fetchLedgerFiltersApi = fetchLedgerFilters;

const availableFilters: CommonFilterProperties[] = [
  {
    name: i18n.t('devices:searchHere'),
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

/**
 * Initial state for Tap Card Ledger slice
 */
const initialState: TapCardLedgerInitialProperties = {
  ledgerEntries: [],
  selectedLedgerEntry: null,
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  ledgerDetailsLoading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

/**
 * Redux slice for Business Tap Card Ledger management
 */
export const tapCardLedgerSlice = createSlice({
  name: 'tap-card-ledger',
  initialState,
  reducers: {
    /**
     * Sets selected ledger entry for side panel or modal
     * @param {TapCardLedgerInitialProperties} state - Current slice state
     * @param {PayloadAction<LedgerEntry | null>} action - Ledger entry payload
     */
    setSelectedLedgerEntry: (state, action: PayloadAction<LedgerEntry | null>) => {
      state.selectedLedgerEntry = action.payload;
    },

    /**
     * Updates ledger list filters
     * @param {TapCardLedgerInitialProperties} state - Current slice state
     * @param {PayloadAction<FilterUpdateProperties>} action - Filter update action
     */
    updateLedgerFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },

    /**
     * Disables the filter updated status flag
     * @param {TapCardLedgerInitialProperties} state - Current slice state
     */
    disableLedgerFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },

    /**
     * Sets ledger sorting parameters
     * @param {TapCardLedgerInitialProperties} state - Current slice state
     * @param {PayloadAction<{ sortBy: 'asc' | 'desc'; sortField: string }>} action - Sorting payload
     */
    setLedgerSortData: (state, action: PayloadAction<{ sortBy: 'asc' | 'desc'; sortField: string }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortField = action.payload.sortField;
    },

    /**
     * Sets sorting parameters (alias matching other modules)
     * @param {TapCardLedgerInitialProperties} state - Current slice state
     * @param {PayloadAction<{ sortBy: 'asc' | 'desc'; sortField: string }>} action - Sorting payload
     */
    setSortData: (state, action: PayloadAction<{ sortBy: 'asc' | 'desc'; sortField: string }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortField = action.payload.sortField;
    },

    /**
     * Resets ledger state to initial values
     * @param {TapCardLedgerInitialProperties} state - Current slice state
     */
    resetLedgerState: (state) => {
      state.ledgerEntries = [];
      state.selectedLedgerEntry = null;
      state.loading = false;
      state.ledgerDetailsLoading = false;
      state.filters = availableFilters;
      state.isFilterUpdated = false;
      state.page = DefaultApiParameters.page;
      state.limit = DefaultApiParameters.limit;
      state.totalPages = 0;
      state.totalResults = 0;
      state.sortBy = null;
      state.sortField = null;
    },
  },
  /**
   * Extra reducers builder for async thunk actions
   * @param {ActionReducerMapBuilder<TapCardLedgerInitialProperties>} builder - Redux Toolkit action builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<TapCardLedgerInitialProperties>) => {
    // Fetch all ledger entries
    builder
      .addCase(fetchAllLedgerEntries.pending, (state: TapCardLedgerInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAllLedgerEntries.fulfilled,
        (
          state: TapCardLedgerInitialProperties,
          action: PayloadAction<APILedgerEntriesResponseInterface>,
        ) => {
          state.loading = false;
          state.ledgerEntries = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchAllLedgerEntries.rejected, (state: TapCardLedgerInitialProperties) => {
        state.loading = false;
      });

    // Refresh ledger entries
    builder.addCase(
      refreshLedgerEntries.fulfilled,
      (
        state: TapCardLedgerInitialProperties,
        action: PayloadAction<APILedgerEntriesResponseInterface>,
      ) => {
        state.ledgerEntries = [...action.payload.results];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      },
    );

    // Single ledger entry details fetch by ID
    builder
      .addCase(fetchLedgerById.pending, (state: TapCardLedgerInitialProperties) => {
        state.ledgerDetailsLoading = true;
      })
      .addCase(
        fetchLedgerById.fulfilled,
        (state: TapCardLedgerInitialProperties, action: PayloadAction<LedgerEntry>) => {
          state.ledgerDetailsLoading = false;
          state.selectedLedgerEntry = action.payload;
          const targetId = action.payload.id || action.payload._id;
          const index = state.ledgerEntries.findIndex(
            (l) => (l.id || l._id) === targetId,
          );
          if (index !== -1) {
            state.ledgerEntries[index] = {
              ...state.ledgerEntries[index],
              ...action.payload,
            };
          }
        },
      )
      .addCase(fetchLedgerById.rejected, (state: TapCardLedgerInitialProperties) => {
        state.ledgerDetailsLoading = false;
      });

    // Ledger filter options
    builder
      .addCase(fetchLedgerFilters.pending, (state: TapCardLedgerInitialProperties) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchLedgerFilters.fulfilled,
        (
          state: TapCardLedgerInitialProperties,
          action: PayloadAction<CommonFilterProperties[]>,
        ) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchLedgerFilters.rejected, (state: TapCardLedgerInitialProperties) => {
        state.filterListLoading = false;
      });
  },
});

export const {
  setSelectedLedgerEntry,
  updateLedgerFilters,
  disableLedgerFilterApplied,
  setLedgerSortData,
  setSortData,
  resetLedgerState,
} = tapCardLedgerSlice.actions;

export default tapCardLedgerSlice.reducer;
